import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState
} from "react";

import { useGetPromptTemplates } from "@/api/agenticAI/usePromptTemplatesApi";
import { PromptTemplate } from "@/types/agenticAI";

import type { AnalysisReportArtifact } from "@lokasi-ai-web-sdk/agent-ui";
import { LokasiAgentChat, useLokasiAgent } from "@lokasi-ai-web-sdk/agent-ui";

import AgenticAIComposer from "./AgenticAIComposer";
import AgenticAIWelcome from "./AgenticAIWelcome";
import {
  AGENTIC_AI_AGENT_TOPIC,
  AGENTIC_AI_DESCRIPTION,
  AGENTIC_AI_HEADING,
  AGENTIC_AI_INPUT_PLACEHOLDER
} from "./constants";
import SuggestedPrompts from "./SuggestedPrompts";

import "./agenticAIChat.css";

type Props = {
  isAgentChatOpen: boolean;
  onOpenAgentChat: () => void;
  /** Host-drawn report card; absent while the report feature is off. */
  renderAnalysisReportArtifact?: (
    artifact: AnalysisReportArtifact
  ) => ReactNode;
};

/**
 * Chat surface inside `LokasiAgentProvider`. Every send — welcome composer,
 * suggested prompt — goes through the shared agent session via
 * `useLokasiAgent().sendPrompt`, so the panel that opens afterwards shows the
 * very same thread.
 */
const AgenticAIChatArea = ({
  isAgentChatOpen,
  onOpenAgentChat,
  renderAnalysisReportArtifact
}: Props) => {
  const { isRunning, sendPrompt } = useLokasiAgent();
  const {
    data: suggestedPrompts = [],
    isFetching: isFetchingSuggestedPrompts,
    isError: hasSuggestedPromptsError,
    refetch: refetchSuggestedPrompts
  } = useGetPromptTemplates(AGENTIC_AI_AGENT_TOPIC);
  const isLoadingSuggestedPrompts =
    isFetchingSuggestedPrompts && suggestedPrompts.length === 0;
  const [draft, setDraft] = useState("");
  const agentChatRef = useRef<HTMLDivElement>(null);
  const promptInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    agentChatRef.current
      ?.querySelector<HTMLTextAreaElement>(
        'textarea[aria-label="Message the assistant"]'
      )
      ?.focus();
  }, [isAgentChatOpen]);

  useEffect(() => {
    if (!isAgentChatOpen) return;

    const transcript =
      agentChatRef.current?.querySelector<HTMLElement>(".lka-transcript");
    if (!transcript) return;

    transcript.scrollTop = transcript.scrollHeight;
  }, [isAgentChatOpen]);

  const startAgentRun = (text: string) => {
    onOpenAgentChat();
    sendPrompt(text).catch(() => undefined);
  };

  const handleSelectSuggestedPrompt = (prompt: PromptTemplate) => {
    setDraft(prompt.prompt_text);
    promptInputRef.current?.focus();
  };

  const handleSubmitPrompt = (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    startAgentRun(text);
  };

  if (isAgentChatOpen) {
    return (
      <div ref={agentChatRef} className="flex min-h-0 flex-1 flex-col p-4 pr-0">
        <LokasiAgentChat
          className="agentic-ai-chat"
          renderAnalysisReportArtifact={renderAnalysisReportArtifact}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-10 bg-white-smoke">
      <div className="mx-auto flex w-full max-w-[720px] flex-1 flex-col justify-center gap-6 py-6">
        <AgenticAIWelcome
          heading={AGENTIC_AI_HEADING}
          description={AGENTIC_AI_DESCRIPTION}
        />

        <form onSubmit={handleSubmitPrompt}>
          <AgenticAIComposer
            ref={promptInputRef}
            value={draft}
            onChange={setDraft}
            disabled={isRunning}
            placeholder={AGENTIC_AI_INPUT_PLACEHOLDER}
          />
        </form>

        {(isLoadingSuggestedPrompts ||
          hasSuggestedPromptsError ||
          suggestedPrompts.length > 0) && (
          <SuggestedPrompts
            prompts={suggestedPrompts}
            disabled={isRunning}
            isLoading={isLoadingSuggestedPrompts}
            isError={hasSuggestedPromptsError}
            onSelect={handleSelectSuggestedPrompt}
            onRetry={() => void refetchSuggestedPrompts()}
          />
        )}
      </div>
    </div>
  );
};

export default AgenticAIChatArea;
